import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { MAX_MAP_NAME_LENGTH, labelFromFilename, mapDisplayName } from '../../sim/map/index.ts';
import { Tooltip } from '../tooltip.tsx';
import { UNTITLED_MAP_NAME } from './persist.ts';

export function MapNameField({
  name,
  fileName,
  onRename,
}: {
  name: string;
  fileName: string;
  onRename: (raw: string) => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const display = mapDisplayName(name, labelFromFilename(fileName) || UNTITLED_MAP_NAME);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const begin = (): void => {
    setDraft(name.trim() ? name : display === UNTITLED_MAP_NAME ? '' : display);
    setEditing(true);
  };

  const cancel = (): void => {
    setEditing(false);
  };

  const commit = (): void => {
    const next = draft.trim();

    if (next === name.trim() || (name.trim() === '' && next === display)) {
      cancel();
      return;
    }

    onRename(draft);
    setEditing(false);
  };

  return (
    <div class="editor-map-name" data-file-name={fileName} data-map-name={display}>
      {editing ? (
        <input
          ref={inputRef}
          class="editor-map-name-input"
          type="text"
          aria-label="Map name"
          value={draft}
          maxlength={MAX_MAP_NAME_LENGTH}
          spellcheck={false}
          onInput={(event) => setDraft((event.target as HTMLInputElement).value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.stopPropagation();
              commit();
            } else if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              cancel();
            }
          }}
          onBlur={cancel}
        />
      ) : (
        <Tooltip label="Rename map" side="bottom">
          <button
            type="button"
            class="editor-map-name-button"
            aria-label="Map name"
            onClick={begin}
          >
            {display}
          </button>
        </Tooltip>
      )}
    </div>
  );
}
